import React, { useState, useMemo, useRef, useLayoutEffect } from "react";
import { NavLink } from "react-router-dom";
import { Bar, CSButton } from "@cadenza/ui"; // keep using local UI primitives
import { BsLayoutSidebar } from "react-icons/bs";
import styles from "./Sidebar.module.css";
import Icon from "./Icon";

const Sidebar = ({
  links = [],
  leadingControls = [],
  trailingControls = [],
  pinnedInitially = false,
}) => {
  const [pinned, setPinned] = useState(pinnedInitially);
  const [hovered, setHovered] = useState(false);

  const expanded = pinned;

  const leading = useMemo(
    () =>
      leadingControls.map(({ Component, key, props }, i) => (
        <Component key={key || `lead-${i}`} {...(props || {})} />
      )),
    [leadingControls]
  );

  const controlRefs = useRef([]);
  const setControlRef = (i) => (el) => {
    if (el) controlRefs.current[i] = el;
  };
  useLayoutEffect(() => {
    controlRefs.current.forEach((wrap) => {
      if (!wrap) return;
      const src = wrap.querySelector("[title], [aria-label]");
      const label =
        src?.getAttribute("title") || src?.getAttribute("aria-label") || "";
      if (label) wrap.setAttribute("data-label", label);
      else wrap.removeAttribute("data-label");
    });
  }, [trailingControls, expanded]);

  const trailingItems = useMemo(
    () =>
      trailingControls.map(({ Component, key, props }, i) => (
        <div
          key={key || `trail-${i}`}
          className={styles.controlItem}
          ref={setControlRef(i)}
        >
          <Component {...(props || {})} />
        </div>
      )),
    [trailingControls]
  );

  const logo = (
    <img
      src="/cslogo.png"
      alt="App"
      title="RAD Tool"
      style={{ display: "block", width: 30, height: 30, objectFit: "contain" }}
      onClick={() => {
        if (!expanded) setPinned(true);
      }}
    />
  );

  const collapsedTrigger = (
    <CSButton
      className="actionButton"
      tabIndex={0}
      onClick={() => setPinned(true)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setPinned(true)}
      title="Open sidebar"
      aria-label="Open sidebar"
    >
      <BsLayoutSidebar className="cs-medium-icon" />
    </CSButton>
  );

  const leftContent = expanded ? logo : hovered ? collapsedTrigger : logo;

  const rightContent = expanded ? (
    <CSButton
      className="actionButton"
      title={pinned ? "Unpin sidebar" : "Pin sidebar"}
      aria-label={pinned ? "Unpin sidebar" : "Pin sidebar"}
      tabIndex={0}
      onClick={() => setPinned((v) => !v)}
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") && setPinned((v) => !v)
      }
    >
      <BsLayoutSidebar className="cs-medium-icon" />
    </CSButton>
  ) : null;

  return (
    <aside
      className={`${styles.sidebar} ${expanded ? styles.expanded : styles.collapsed}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label="Primary"
      aria-expanded={expanded}
    >
      <Bar
        className={styles.header}
        leftContent={leftContent}
        centreContent={null}
        rightContent={rightContent}
      />

      {leading.length > 0 && (
        <div className={styles.controlsTop}>{leading}</div>
      )}

      <nav className={styles.nav}>
        <ul className={styles.linkList}>
          {links.map(({ to, label, icon: IconComp, filled }, i) => (
            <li key={to || i} className={styles.linkItem}>
              <NavLink
                to={to}
                className={({ isActive }) =>
                  `${styles.link} ${isActive ? styles.active : ""}`
                }
                title={label}
              >
                {IconComp ? (
                  <Icon
                    Comp={IconComp}
                    className={styles.linkIcon}
                    filled={filled}
                  />
                ) : null}
                {expanded && (
                  <span className={styles.linkText}>{label}</span>
                )}{" "}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {trailingControls.length > 0 && (
        <div className={styles.controlsBottom}>{trailingItems}</div>
      )}
    </aside>
  );
};

export default Sidebar;
